import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CreditCard,
  Calendar,
  CheckCircle,
  AlertCircle,
  Download,
  TrendingUp,
  X,
  Loader,
  Package,
  MessageSquare,
  Eye,
  Mail,
  FileText,
} from 'lucide-react';
import { billingService } from '../../services/billingService';
import { authService } from '../../services/authService';
import { apiClient } from '../../services/apiClient';
import PlanChangeConfirmModal from '../../components/PlanChangeConfirmModal';
import { useDateFormat } from '../../hooks/useDateFormat';

const Billing = () => {
  const { t, i18n } = useTranslation();
  // All billing dates below are java.time.LocalDate on the backend -> date-only, no tz shift.
  const { formatDateOnly } = useDateFormat();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState(null);
  const [cmmsData, setCmmsData] = useState(null);
  const [chatbotData, setChatbotData] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState(null);

  // Modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeServiceType, setUpgradeServiceType] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('MONTHLY');
  const [annualDiscount, setAnnualDiscount] = useState(null);

  // Auto-open upgrade modal from ?upgrade= query param
  const [pendingUpgrade, setPendingUpgrade] = useState(null);

  // Cancel subscription state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelServiceType, setCancelServiceType] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Overage toggle loading state
  const [overageToggleLoading, setOverageToggleLoading] = useState({});

  // Auto-renewal toggle loading state
  const [togglingRenewal, setTogglingRenewal] = useState(null);

  // Invoice preview modal
  const [previewModal, setPreviewModal] = useState(null); // { html, title }
  const [previewLoading2, setPreviewLoading2] = useState(null); // invoiceNumber + type
  const [resendLoading2, setResendLoading2] = useState(null); // invoiceNumber being resent
  const [payInvoiceLoading, setPayInvoiceLoading] = useState(null); // invoiceNumber being paid
  const [payInvoiceError, setPayInvoiceError] = useState(null);

  // Chatbot message blocks state
  const [availableBlocks, setAvailableBlocks] = useState([]);
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [blockBuying, setBlockBuying] = useState(false);

  // CMMS slot packs state
  const [slotModal, setSlotModal] = useState({ open: false, resourceType: null });
  const [availableSlotPacks, setAvailableSlotPacks] = useState([]);
  const [slotBuying, setSlotBuying] = useState(false);

  // Paid→Paid plan change state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmPreview, setConfirmPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [changePlanSubmitting, setChangePlanSubmitting] = useState(false);
  const [changePlanSuccess, setChangePlanSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    loadBillingData();
  }, []);

  // Sync fresh subscription status back to localStorage so PortalLayout's overlay reacts immediately
  useEffect(() => {
    if (cmmsData?.status != null) {
      localStorage.setItem('cmmsSubscriptionStatus', cmmsData.status);
      window.dispatchEvent(new CustomEvent('subscriptionStatusUpdated'));
    }
  }, [cmmsData?.status]);

  useEffect(() => {
    if (chatbotData?.status != null) {
      localStorage.setItem('chatbotSubscriptionStatus', chatbotData.status);
      window.dispatchEvent(new CustomEvent('subscriptionStatusUpdated'));
    }
  }, [chatbotData?.status]);

  // Handle Stripe Checkout success redirect and ?upgrade= deep-link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setChangePlanSuccess(true);
      window.history.replaceState({}, '', window.location.pathname);
      loadBillingData();
    }
    const upgradeParam = params.get('upgrade')?.toUpperCase();
    if (upgradeParam === 'CMMS' || upgradeParam === 'CHATBOT') {
      setPendingUpgrade(upgradeParam);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Once billing data has loaded and companyId is known, open the pending upgrade modal
  useEffect(() => {
    if (pendingUpgrade && companyId && !loading) {
      setPendingUpgrade(null);
      openUpgradeModal(pendingUpgrade);
    }
  }, [pendingUpgrade, companyId, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBillingData = async () => {
    try {
      setLoading(true);
      setError(null);

      const user = authService.getCurrentUser();
      const id = user?.companyId || authService.getCompanyId();

      if (!id) {
        throw new Error('Company ID not found');
      }

      setCompanyId(id);

      // Fetch billing summaries only for services the company is subscribed to
      const hasCmms = localStorage.getItem('hasCmmsSubscription') === 'true';
      const hasChatbot = localStorage.getItem('hasChatbotSubscription') === 'true';
      const promises = [];

      if (hasCmms) {
        promises.push(
          billingService.getSummary(id, 'CMMS')
            .then(data => setCmmsData(data))
            .catch(() => setCmmsData(null))
        );
      } else {
        setCmmsData(null);
      }

      if (hasChatbot) {
        promises.push(
          billingService.getSummary(id, 'CHATBOT')
            .then(async (data) => {
              setChatbotData(data);
              // Also fetch available blocks for chatbot
              try {
                const blocks = await billingService.getAvailableChatbotBlocks(id);
                setAvailableBlocks(blocks || []);
              } catch {
                // non-critical
              }
            })
            .catch(() => setChatbotData(null))
        );
      } else {
        setChatbotData(null);
      }

      // Fetch invoices
      promises.push(
        billingService.getInvoices(id)
          .then(data => setInvoices(data || []))
          .catch(() => setInvoices([]))
      );

      await Promise.all(promises);
    } catch (err) {
      console.error('Error loading billing data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const isTrialSubscription = (serviceType) => {
    if (serviceType === 'CMMS') return cmmsData?.status === 'TRIAL';
    if (serviceType === 'CHATBOT') return chatbotData?.status === 'TRIAL';
    return false;
  };

  const openUpgradeModal = async (serviceType) => {
    try {
      setUpgradeServiceType(serviceType);
      setShowUpgradeModal(true);

      // Fetch plans for the service type
      const endpoint = serviceType === 'CMMS'
        ? '/api/subscriptions/cmms/plans'
        : '/api/subscriptions/chatbot/plans';

      const plansData = await apiClient.get(endpoint);
      setPlans(plansData || []);
      setSelectedPlan(null);
      const ref = (plansData || []).find(p => Number(p.monthlyPrice) > 0 && Number(p.annualPrice) > 0);
      if (ref) {
        const pct = Math.round((1 - Number(ref.annualPrice) / (Number(ref.monthlyPrice) * 12)) * 100);
        setAnnualDiscount(pct > 0 ? pct : null);
      } else {
        setAnnualDiscount(null);
      }
    } catch (err) {
      console.error('Error loading plans:', err);
      alert('Failed to load plans. Please try again.');
    }
  };

  const handleUpgradePlan = () => {
    if (!selectedPlan) return;
    setShowUpgradeModal(false);
    navigate('/portal/checkout', {
      state: {
        plan: selectedPlan,
        billingCycle: billingPeriod.toLowerCase(),
        serviceType: upgradeServiceType,
      },
    });
  };

  const handleConfirmChangePlan = async () => {
    if (!selectedPlan) return;
    try {
      setChangePlanSubmitting(true);
      await billingService.changePlan(companyId, {
        serviceType: upgradeServiceType,
        planId: selectedPlan.id,
        billingPeriod,
      });
      setShowConfirmModal(false);
      setChangePlanSuccess(true);
      loadBillingData();
    } catch (err) {
      console.error('Error changing plan:', err);
      alert('Failed to change plan. Please try again.');
    } finally {
      setChangePlanSubmitting(false);
    }
  };

  const openCancelModal = (serviceType) => {
    setCancelServiceType(serviceType);
    setShowCancelModal(true);
  };

  const handleCancelSubscription = async () => {
    try {
      setCancelLoading(true);
      await billingService.cancelSubscription(companyId, cancelServiceType);
      if (cancelServiceType === 'CMMS') localStorage.setItem('hasCmmsSubscription', 'false');
      if (cancelServiceType === 'CHATBOT') localStorage.setItem('hasChatbotSubscription', 'false');
      setShowCancelModal(false);
      // Reload the page so the sidebar menu reflects the removed subscription
      window.location.reload();
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      alert('Failed to cancel subscription. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleToggleOverage = async (serviceType, enabled) => {
    setOverageToggleLoading(prev => ({ ...prev, [serviceType]: true }));
    try {
      await billingService.toggleOverage(companyId, serviceType, enabled);
      if (serviceType === 'CMMS') {
        setCmmsData(prev => ({ ...prev, overageEnabled: enabled }));
      } else {
        setChatbotData(prev => ({ ...prev, overageEnabled: enabled }));
      }
    } catch (err) {
      console.error('Failed to toggle overage:', err);
      alert('Failed to update overage setting. Please try again.');
    } finally {
      setOverageToggleLoading(prev => ({ ...prev, [serviceType]: false }));
    }
  };

  const handleBuyBlock = async (blockDefId) => {
    setBlockBuying(true);
    try {
      await billingService.buyChatbotBlock(companyId, blockDefId);
      setBlockModalOpen(false);
      const updated = await billingService.getSummary(companyId, 'CHATBOT');
      setChatbotData(updated);
    } catch (err) {
      alert(err.message || 'Failed to purchase message block');
    } finally {
      setBlockBuying(false);
    }
  };

  const openSlotModal = async (resourceType, label) => {
    try {
      const packs = await billingService.getAvailableCmmsSlotPacks(companyId, resourceType);
      setAvailableSlotPacks(packs || []);
    } catch {
      setAvailableSlotPacks([]);
    }
    setSlotModal({ open: true, resourceType, label });
  };

  const handleBuySlot = async (slotPackDefId) => {
    setSlotBuying(true);
    try {
      await billingService.buyCmmsSlot(companyId, slotPackDefId);
      setSlotModal({ open: false, resourceType: null });
      const updated = await billingService.getSummary(companyId, 'CMMS');
      setCmmsData(updated);
    } catch (err) {
      alert(err.message || 'Failed to purchase slot pack');
    } finally {
      setSlotBuying(false);
    }
  };

  const handlePreview = async (type, invoice) => {
    const key = invoice.id + '-' + type;
    setPreviewLoading2(key);
    try {
      const html = type === 'invoice'
        ? await billingService.previewInvoice(companyId, invoice.id)
        : await billingService.previewReceipt(companyId, invoice.id);
      setPreviewModal({ html, title: type === 'invoice' ? `Invoice ${invoice.id}` : `Receipt ${invoice.receiptNumber || invoice.id}` });
    } catch (err) {
      alert('Failed to load preview. Please try again.');
    } finally {
      setPreviewLoading2(null);
    }
  };

  const handlePayInvoice = async (invoice) => {
    setPayInvoiceLoading(invoice.id);
    setPayInvoiceError(null);
    try {
      await billingService.payInvoice(companyId, invoice.id);
      // Refresh invoices after payment
      const updated = await billingService.getInvoices(companyId);
      setInvoices(updated || []);
    } catch (err) {
      setPayInvoiceError(err.message || 'Payment failed. Please try again.');
    } finally {
      setPayInvoiceLoading(null);
    }
  };

  const handleResendEmail = async (invoice) => {
    setResendLoading2(invoice.id);
    try {
      await billingService.resendEmail(companyId, invoice.id);
      alert('Email sent successfully!');
    } catch (err) {
      alert('Failed to send email. Please try again.');
    } finally {
      setResendLoading2(null);
    }
  };

  const handleToggleAutoRenewal = async (serviceType, enabled) => {
    setTogglingRenewal(serviceType);
    try {
      await billingService.toggleAutoRenewal(companyId, serviceType, enabled);
      if (serviceType === 'CMMS') {
        const updated = await billingService.getSummary(companyId, 'CMMS');
        setCmmsData(updated);
      } else {
        const updated = await billingService.getSummary(companyId, 'CHATBOT');
        setChatbotData(updated);
      }
    } catch (err) {
      alert(err.message || 'Failed to update auto-renewal setting');
    } finally {
      setTogglingRenewal(null);
    }
  };

  const handleCancelSlot = async (slotPurchaseId) => {
    if (!window.confirm('Cancel auto-renewal for this slot pack? It will continue to work until it expires.')) return;
    try {
      await billingService.cancelSlot(companyId, slotPurchaseId);
      const updated = await billingService.getSummary(companyId, 'CMMS');
      setCmmsData(updated);
    } catch (err) {
      alert(err.message || 'Failed to cancel slot');
    }
  };

  const handleCancelBlock = async (blockPurchaseId) => {
    if (!window.confirm('Cancel auto-renewal for this message block? It will continue to work until it expires.')) return;
    try {
      await billingService.cancelBlock(companyId, blockPurchaseId);
      const updated = await billingService.getSummary(companyId, 'CHATBOT');
      setChatbotData(updated);
    } catch (err) {
      alert(err.message || 'Failed to cancel block');
    }
  };

  const renderUsageMeter = (label, current, limit, overage = 0) => {
    const percentage = limit > 0 ? Math.min((current / limit) * 100, 100) : 0;
    const hasOverage = overage > 0;

    return (
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-2">
          <span className="font-medium text-gray-700">{label}</span>
          <span className="text-gray-600">
            {current} / {limit}
            {hasOverage && (
              <span className="ml-2 text-red-600 font-semibold">
                (+${overage.toFixed(2)} overage)
              </span>
            )}
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              hasOverage ? 'bg-red-500' : 'bg-primary-600'
            }`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  const renderCurrentPlanCard = (data, serviceType) => {
    if (!data) return null;

    const icon = serviceType === 'CMMS' ? Package : MessageSquare;
    const Icon = icon;
    const bgGradient = serviceType === 'CMMS'
      ? 'bg-gradient-to-br from-primary-500 to-primary-600'
      : 'bg-gradient-to-br from-blue-500 to-blue-600';

    const status = data.status;
    const isExpiredOrSuspended = status === 'EXPIRED' || status === 'SUSPENDED';
    const isActive = !isExpiredOrSuspended && status !== 'CANCELLED';

    const statusBadge = status === 'EXPIRED' || status === 'SUSPENDED' ? (
      <span className="px-3 py-1 bg-red-100 text-red-700 text-sm font-semibold rounded-full flex items-center gap-1">
        <AlertCircle className="w-4 h-4" />
        {status === 'EXPIRED' ? t('billing.statusExpired') : t('billing.statusSuspended')}
      </span>
    ) : status === 'GRACE_PERIOD' ? (
      <span className="px-3 py-1 bg-amber-100 text-amber-700 text-sm font-semibold rounded-full flex items-center gap-1">
        <AlertCircle className="w-4 h-4" />
        {t('billing.statusGracePeriod')}
      </span>
    ) : status === 'TRIAL' ? (
      <span className="px-3 py-1 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full flex items-center gap-1">
        <CheckCircle className="w-4 h-4" />
        {t('billing.statusTrial')}
      </span>
    ) : (
      <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-semibold rounded-full flex items-center gap-1">
        <CheckCircle className="w-4 h-4" />
        {t('common.active')}
      </span>
    );

    return (
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 ${bgGradient} rounded-lg flex items-center justify-center`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">{serviceType}</h3>
              <p className="text-sm text-gray-600">{data.planName} {t('billing.planSuffix')}</p>
            </div>
          </div>
          {statusBadge}
        </div>

        <div className="space-y-3 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">{t('billing.billingPeriod')}</span>
            <span className="font-semibold text-gray-900">
              {data.billingPeriod === 'MONTHLY' ? t('billing.monthly') : t('billing.annual')}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">{t('billing.basePrice')}</span>
            <span className="font-semibold text-gray-900">${data.basePrice?.toFixed(2)}</span>
          </div>
          {!isExpiredOrSuspended && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t('billing.nextBillingDate')}</span>
              <span className="font-semibold text-gray-900">
                {data.nextBillingDate ? formatDateOnly(data.nextBillingDate) : 'N/A'}
              </span>
            </div>
          )}
        </div>

        {/* Auto-renewal toggle — only for active subscriptions */}
        {isActive && (
          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm text-gray-600">{t('billing.autoRenewal')}</span>
            <button
              dir="ltr"
              onClick={() => handleToggleAutoRenewal(serviceType, !data.autoRenewal)}
              disabled={togglingRenewal === serviceType}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                ${data.autoRenewal ? 'bg-green-500' : 'bg-gray-300'}
                ${togglingRenewal === serviceType ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
                ${data.autoRenewal
                  ? (i18n.dir() === 'rtl' ? 'translate-x-1' : 'translate-x-6')
                  : (i18n.dir() === 'rtl' ? 'translate-x-6' : 'translate-x-1')}`} />
            </button>
            <span className="text-sm text-gray-500">{data.autoRenewal ? t('billing.on') : t('billing.off')}</span>
          </div>
        )}

        <button
          onClick={() => openUpgradeModal(serviceType)}
          className={`w-full flex items-center justify-center gap-2 ${
            isExpiredOrSuspended ? 'btn btn-primary' : 'btn btn-outline hover:bg-primary-50'
          }`}
        >
          <TrendingUp className="w-4 h-4" />
          {isExpiredOrSuspended ? t('billing.renewNow') : t('billing.upgradePlan')}
        </button>

        {isActive && (
          <button
            onClick={() => openCancelModal(serviceType)}
            className="w-full mt-2 text-sm text-red-500 hover:text-red-700 hover:underline transition-colors py-1"
          >
            {t('billing.cancelSubscription')}
          </button>
        )}
      </div>
    );
  };

  const renderUsageSection = (data, serviceType) => {
    if (!data) return null;

    return (
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {t('billing.planConsumption', { service: serviceType === 'CMMS' ? 'CMMS' : 'Chatbot' })}
        </h3>

        {serviceType === 'CMMS' && (
          <>
            {data.includedUsers > 0 && renderUsageMeter(
              t('cmms.team_users'),
              data.currentUsers || 0,
              data.includedUsers || 0
            )}
            {data.includedAssets > 0 && renderUsageMeter(
              t('cmms.assets'),
              data.currentAssets || 0,
              data.includedAssets || 0
            )}
            {data.includedLocations > 0 && renderUsageMeter(
              t('cmms.locations'),
              data.currentLocations || 0,
              data.includedLocations || 0
            )}
            {!data.includedUsers && !data.includedAssets && !data.includedLocations && (
              <p className="text-sm text-gray-400 text-center py-4">{t('billing.noUsageLimits')}</p>
            )}
          </>
        )}

        {serviceType === 'CHATBOT' && (
          renderUsageMeter(
            t('billing.messagesThisMonth'),
            data.currentUsage || 0,
            data.includedLimit || 0
          )
        )}
      </div>
    );
  };

  const renderOverageSection = (data, serviceType) => {
    if (!data) return null;

    const isOverageEnabled = data.overageEnabled !== false;
    const isToggling = overageToggleLoading[serviceType];
    const serviceLabel = serviceType === 'CMMS' ? 'CMMS' : 'Chatbot';
    const hasPendingOverage = Number(data.pendingOverageAmount) > 0;

    return (
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        {/* Header with toggle */}
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('billing.overageTitle', { service: serviceLabel })}</h3>
            <p className="text-sm text-gray-500 mt-0.5">{t('billing.overageSubtitle')}</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-sm text-gray-600">{t('billing.allowOverage')}</span>
            <button
              onClick={() => handleToggleOverage(serviceType, !isOverageEnabled)}
              disabled={isToggling}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                isOverageEnabled ? 'bg-primary-600' : 'bg-gray-300'
              } ${isToggling ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              title={isOverageEnabled ? 'Click to disable overage' : 'Click to enable overage'}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  isOverageEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-xs font-semibold ${isOverageEnabled ? 'text-primary-600' : 'text-gray-400'}`}>
              {isOverageEnabled ? t('billing.enabled') : t('billing.disabled')}
            </span>
          </div>
        </div>

        {/* Overage disabled warning */}
        {!isOverageEnabled && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            {t('billing.overageDisabledWarning')}
          </div>
        )}

        {/* Chatbot overage metrics */}
        {serviceType === 'CHATBOT' && (() => {
          const overageCount = data.overageMessagesUsed || 0;
          const pricePerMsg = Number(data.overagePricePerMessage || 0);
          const overageCost = Number(data.overageCost || 0);
          const hasOverage = overageCount > 0;
          return (
            <div className="space-y-0 divide-y divide-gray-100">
              <div className="flex justify-between items-center py-2.5">
                <span className="text-sm text-gray-600">{t('billing.msgsBeyondLimit')}</span>
                <span className={`text-sm font-semibold ${hasOverage ? 'text-red-600' : 'text-gray-400'}`}>
                  {hasOverage ? overageCount.toLocaleString() + ' ' + t('billing.messages') : t('billing.noneThisPeriod')}
                </span>
              </div>
              {hasOverage && (
                <div className="flex justify-between items-center py-2.5">
                  <span className="text-sm text-gray-600">{t('billing.pricePerOverageMsg')}</span>
                  <span className="text-sm text-gray-700">
                    ${pricePerMsg.toFixed(4)}
                  </span>
                </div>
              )}
              {hasOverage && (
                <div className="flex justify-between items-center py-2.5">
                  <span className="text-sm font-medium text-gray-700">{t('billing.overageCostPeriod')}</span>
                  <span className="text-sm font-bold text-red-600">
                    ${overageCost.toFixed(2)}
                  </span>
                </div>
              )}
              {hasPendingOverage && (
                <div className="flex justify-between items-center py-2.5">
                  <span className="text-sm font-medium text-gray-700">{t('billing.pendingOverageCharges')}</span>
                  <span className="text-sm font-bold text-red-600">
                    ${Number(data.pendingOverageAmount).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          );
        })()}

        {/* CMMS overage metrics */}
        {serviceType === 'CMMS' && (
          <div className="space-y-0 divide-y divide-gray-100">
            {data.overageItems?.length > 0 ? (
              data.overageItems.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center py-2.5">
                  <span className="text-sm text-gray-600">
                    {item.resourceType}: {item.quantity} unit{item.quantity !== 1 ? 's' : ''} × ${Number(item.unitPrice).toFixed(2)}
                  </span>
                  <span className="text-sm font-semibold text-red-600">
                    ${Number(item.totalAmount).toFixed(2)}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-400 text-center py-3">{t('billing.noOverageUsage')}</p>
            )}
            {hasPendingOverage && (
              <div className="flex justify-between items-center py-2.5 pt-3">
                <span className="text-sm font-semibold text-gray-700">{t('billing.totalPendingOverage')}</span>
                <span className="text-sm font-bold text-red-600">
                  ${Number(data.pendingOverageAmount).toFixed(2)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderPendingOverageCard = () => {
    const cmmsOverage = cmmsData?.pendingOverageAmount || 0;
    const chatbotOverage = chatbotData?.pendingOverageAmount || 0;
    const totalOverage = cmmsOverage + chatbotOverage;

    if (totalOverage <= 0) return null;

    return (
      <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-6 mb-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-amber-900 mb-2">
              {t('billing.pendingOverageTitle')}
            </h3>
            <p className="text-amber-800 text-sm mb-4">
              {t('billing.overageExceededDesc')}
            </p>

            <div className="bg-white rounded-lg p-4 space-y-2">
              <div className="flex justify-between font-semibold text-lg text-gray-900">
                <span>{t('billing.totalOverageDue')}</span>
                <span className="text-red-600">${totalOverage.toFixed(2)}</span>
              </div>

              <div className="border-t border-gray-200 pt-2 mt-2 space-y-1">
                {cmmsData?.overageItems?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm text-gray-700">
                    <span>CMMS - {item.resourceType}: {item.quantity} × ${item.unitPrice}</span>
                    <span>${item.totalAmount.toFixed(2)}</span>
                  </div>
                ))}
                {chatbotData?.overageItems?.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm text-gray-700">
                    <span>Chatbot - {item.resourceType}: {item.quantity} × ${item.unitPrice}</span>
                    <span>${item.totalAmount.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderInvoicesTable = () => {
    // Stored card info from either summary (card is per-company, same for both)
    const pmBrand = cmmsData?.paymentMethodBrand || chatbotData?.paymentMethodBrand;
    const pmLast4 = cmmsData?.paymentMethodLast4 || chatbotData?.paymentMethodLast4;

    if (invoices.length === 0) {
      return (
        <div className="text-center py-12 text-gray-500">
          <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-400" />
          <p className="font-medium">{t('billing.noInvoices')}</p>
          <p className="text-sm mt-1">{t('billing.billingHistoryDesc')}</p>
        </div>
      );
    }

    return (
      <div>
        {/* Stored card info banner */}
        {pmBrand && pmLast4 && (
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 mb-4">
            <CreditCard className="w-4 h-4 text-gray-400" />
            <span>Payment method on file: <strong>{pmBrand.charAt(0).toUpperCase() + pmBrand.slice(1)} •••• {pmLast4}</strong></span>
          </div>
        )}

        {/* Pay-now error banner */}
        {payInvoiceError && (
          <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2 mb-4">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{payInvoiceError}</span>
            <button onClick={() => setPayInvoiceError(null)} className="ml-auto text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.date')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.description')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('billing.amount')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.status')}</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {invoices.map((invoice) => {
                const isOpen = invoice.status === 'open';
                return (
                  <tr key={invoice.id} className={isOpen ? 'bg-amber-50' : ''}>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      {invoice.date ? formatDateOnly(invoice.date) : '—'}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {invoice.description || 'Subscription'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      ${Number(invoice.amount || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      {invoice.dueDate
                        ? <span className={isOpen ? 'font-medium text-amber-700' : ''}>{formatDateOnly(invoice.dueDate)}</span>
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          invoice.status === 'paid'
                            ? 'bg-green-100 text-green-800'
                            : invoice.status === 'open'
                            ? 'bg-amber-100 text-amber-800'
                            : invoice.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {invoice.status === 'open' ? 'Due' : invoice.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Pay Now — only for OPEN invoices */}
                        {isOpen && (
                          <button
                            onClick={() => handlePayInvoice(invoice)}
                            disabled={payInvoiceLoading === invoice.id}
                            className="flex items-center gap-1 bg-primary-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50"
                            title={pmBrand ? `Pay with ${pmBrand} •••• ${pmLast4}` : 'Pay now'}
                          >
                            {payInvoiceLoading === invoice.id
                              ? <Loader className="w-3 h-3 animate-spin" />
                              : <CreditCard className="w-3 h-3" />}
                            Pay Now
                          </button>
                        )}
                        {/* Preview Invoice */}
                        <button
                          onClick={() => handlePreview('invoice', invoice)}
                          disabled={previewLoading2 === invoice.id + '-invoice'}
                          className="flex items-center gap-1 text-primary-600 hover:text-primary-800 transition-colors disabled:opacity-50"
                          title="Preview Invoice"
                        >
                          {previewLoading2 === invoice.id + '-invoice'
                            ? <Loader className="w-4 h-4 animate-spin" />
                            : <Eye className="w-4 h-4" />}
                          <span className="text-xs">{t('billing.invoice')}</span>
                        </button>
                        {/* Preview Receipt */}
                        {invoice.receiptNumber && (
                          <button
                            onClick={() => handlePreview('receipt', invoice)}
                            disabled={previewLoading2 === invoice.id + '-receipt'}
                            className="flex items-center gap-1 text-emerald-600 hover:text-emerald-800 transition-colors disabled:opacity-50"
                            title="Preview Receipt"
                          >
                            {previewLoading2 === invoice.id + '-receipt'
                              ? <Loader className="w-4 h-4 animate-spin" />
                              : <FileText className="w-4 h-4" />}
                            <span className="text-xs">{t('billing.receipt')}</span>
                          </button>
                        )}
                        {/* Resend Email */}
                        <button
                          onClick={() => handleResendEmail(invoice)}
                          disabled={resendLoading2 === invoice.id}
                          className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
                          title="Resend Email"
                        >
                          {resendLoading2 === invoice.id
                            ? <Loader className="w-4 h-4 animate-spin" />
                            : <Mail className="w-4 h-4" />}
                          <span className="text-xs">{t('billing.emailAction')}</span>
                        </button>
                        {/* Download PDF if available */}
                        {invoice.pdfUrl && (
                          <a
                            href={invoice.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-gray-500 hover:text-gray-700"
                            title="Download PDF"
                          >
                            <Download className="w-4 h-4" />
                            <span className="text-xs">PDF</span>
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderPreviewModal = () => {
    if (!previewModal) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl flex flex-col" style={{ height: '90vh' }}>
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">{previewModal.title}</h2>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  const win = window.open('', '_blank');
                  win.document.write(previewModal.html);
                  win.document.close();
                  win.print();
                }}
                className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-800 font-medium"
              >
                <Download className="w-4 h-4" />
                {t('billing.printSave')}
              </button>
              <button
                onClick={() => setPreviewModal(null)}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>
          {/* iframe */}
          <iframe
            srcDoc={previewModal.html}
            title={previewModal.title}
            className="flex-1 w-full border-0 rounded-b-xl"
          />
        </div>
      </div>
    );
  };

  const renderUpgradeModal = () => {
    if (!showUpgradeModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Modal Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">
              {t('billing.upgradePlanTitle', { service: upgradeServiceType })}
            </h2>
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Billing Period Toggle */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => setBillingPeriod('MONTHLY')}
                className={`px-6 py-2 rounded-lg font-semibold transition-all ${
                  billingPeriod === 'MONTHLY'
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('billing.monthly')}
              </button>
              <button
                onClick={() => setBillingPeriod('ANNUAL')}
                className={`px-6 py-2 rounded-lg font-semibold transition-all ${
                  billingPeriod === 'ANNUAL'
                    ? 'bg-primary-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('billing.annual')}
                {annualDiscount != null && (
                  <span className="ms-4 text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                    {t('billing.saveDiscount', { pct: annualDiscount })}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Plans Grid */}
          <div className="p-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {plans.map((plan) => {
                const price = billingPeriod === 'MONTHLY' ? plan.monthlyPrice : plan.annualPrice;
                const isSelected = selectedPlan?.id === plan.id;
                const currentPlanName = upgradeServiceType === 'CMMS'
                  ? cmmsData?.planName
                  : chatbotData?.planName;
                const isCurrentPlan = (plan.planName || plan.name) === currentPlanName;

                return (
                  <div
                    key={plan.id}
                    onClick={() => !isCurrentPlan && setSelectedPlan(plan)}
                    className={`border-2 rounded-lg p-5 transition-all ${
                      isCurrentPlan
                        ? 'border-green-400 bg-green-50 cursor-not-allowed opacity-80'
                        : isSelected
                        ? 'border-primary-600 bg-primary-50 shadow-lg cursor-pointer'
                        : 'border-gray-200 hover:border-primary-300 hover:shadow-md cursor-pointer'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-bold text-gray-900">{plan.planName || plan.name}</h3>
                      {isCurrentPlan ? (
                        <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full whitespace-nowrap flex-shrink-0">
                          <CheckCircle className="w-3.5 h-3.5" />
                          {t('billing.currentPlan')}
                        </span>
                      ) : isSelected ? (
                        <CheckCircle className="w-6 h-6 text-primary-600 flex-shrink-0" />
                      ) : null}
                    </div>

                    <div className="mb-2">
                      <span className="text-3xl font-bold text-gray-900">${price}</span>
                      <span className="text-gray-600 text-sm">
                        {billingPeriod === 'MONTHLY' ? t('billing.perMonth') : t('billing.perYear')}
                      </span>
                    </div>

                    <p className="text-sm text-gray-500 mb-3">{plan.description}</p>

                    {/* Included limits */}
                    <div className="bg-gray-50 rounded-lg px-3 py-2 mb-3 space-y-1">
                      {upgradeServiceType === 'CHATBOT' && (
                        <>
                          {plan.messagesPerMonth != null && (
                            <div className="text-sm font-semibold text-primary-700">
                              {t('billing.messagesPerMonth', { count: Number(plan.messagesPerMonth).toLocaleString() })}
                            </div>
                          )}
                          {plan.maxChatbots != null && (
                            <div className="text-sm text-gray-700">{t('billing.chatbotsIncluded', { count: plan.maxChatbots })}</div>
                          )}
                          {plan.maxDocumentsPerChatbot != null && (
                            <div className="text-sm text-gray-700">{t('billing.docsIncluded', { count: plan.maxDocumentsPerChatbot })}</div>
                          )}
                          {plan.includedAgents != null && (
                            <div className="text-sm text-gray-700">{t('billing.agentsIncluded', { count: plan.includedAgents })}</div>
                          )}
                        </>
                      )}
                      {upgradeServiceType === 'CMMS' && (
                        <>
                          {plan.includedUsers != null && (
                            <div className="text-sm text-gray-700">{t('billing.usersIncluded', { count: plan.includedUsers })}</div>
                          )}
                          {plan.includedAssets != null && (
                            <div className="text-sm text-gray-700">{t('billing.assetsIncluded', { count: plan.includedAssets })}</div>
                          )}
                          {plan.includedLocations != null && (
                            <div className="text-sm text-gray-700">{t('billing.locationsIncluded', { count: plan.includedLocations })}</div>
                          )}
                          {plan.includedBranches != null && (
                            <div className="text-sm text-gray-700">{t('billing.branchesIncluded', { count: plan.includedBranches })}</div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Features */}
                    <div className="space-y-1.5">
                      {(() => {
                        let featureList = plan.features ?? [];
                        if (typeof featureList === 'string') {
                          try { featureList = JSON.parse(featureList); } catch { featureList = []; }
                        }
                        return Array.isArray(featureList) ? featureList.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </div>
                        )) : null;
                      })()}
                    </div>

                    {/* Overage — chatbot only */}
                    {upgradeServiceType === 'CHATBOT' && plan.overageEnabled && (() => {
                      const items = plan.overagePricePerMessage != null
                        ? [t('billing.overageAdditionalMsgs', { price: Number(plan.overagePricePerMessage).toFixed(5) })]
                        : plan.pricePerAdditional1k != null
                          ? [t('billing.overageAdditionalMsgsPer1k', { price: Number(plan.pricePerAdditional1k).toFixed(2) })]
                          : [];
                      return items.length > 0 ? (
                        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <AlertCircle className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                            <p className="text-xs font-semibold text-amber-800">{t('billing.additionalUsageCosts')}</p>
                          </div>
                          {items.map((item, i) => (
                            <p key={i} className="text-xs text-amber-700 leading-relaxed">{item}</p>
                          ))}
                        </div>
                      ) : null;
                    })()}
                  </div>
                );
              })}
            </div>

            {plans.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Loader className="w-8 h-8 mx-auto mb-3 animate-spin text-primary-600" />
                <p>{t('billing.loadingPlans')}</p>
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
            <button
              onClick={() => setShowUpgradeModal(false)}
              className="btn btn-outline"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleUpgradePlan}
              disabled={!selectedPlan}
              className="btn btn-primary flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              {t('billing.activatePlan')}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-6 h-6 text-red-600" />
          <div>
            <h3 className="font-semibold text-red-900">{t('billing.errorTitle')}</h3>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const hasAnySubscription = cmmsData || chatbotData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{t('nav.billing')}</h1>
        <p className="text-gray-600 mt-2">{t('billing.subtitle')}</p>
      </div>

      {/* Subscription expiry banner */}
      {(cmmsData?.status === 'EXPIRED' || cmmsData?.status === 'GRACE_PERIOD' || cmmsData?.status === 'SUSPENDED' ||
        chatbotData?.status === 'EXPIRED' || chatbotData?.status === 'GRACE_PERIOD' || chatbotData?.status === 'SUSPENDED') && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-amber-600 text-xl">⚠️</span>
            <div>
              <p className="font-semibold text-amber-800">{t('billing.subscriptionExpired')}</p>
              <p className="text-sm text-amber-700">{t('billing.renewToResume')}</p>
            </div>
          </div>
          <span className="px-6 py-3 bg-amber-600 text-white text-base font-semibold rounded-lg cursor-default select-none">
            {t('billing.renewNow')}
          </span>
        </div>
      )}

      {/* Plan Change Success Banner */}
      {changePlanSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <span className="text-green-800 font-medium">{successMessage || t('billing.planUpdated')}</span>
          </div>
          <button onClick={() => { setChangePlanSuccess(false); setSuccessMessage(''); }} className="text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* No Subscriptions State */}
      {!hasAnySubscription && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 text-center">
          <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-amber-900 mb-2">
            {t('billing.noSubscriptions')}
          </h2>
          <p className="text-amber-700 mb-6">
            {t('billing.noSubscriptionsDesc')}
          </p>
          <button className="btn btn-primary">
            {t('billing.explorePlans')}
          </button>
        </div>
      )}

      {/* Pending Overage Alert */}
      {renderPendingOverageCard()}

      {/* Current Plans */}
      {hasAnySubscription && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('billing.currentPlans')}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {renderCurrentPlanCard(cmmsData, 'CMMS')}
            {renderCurrentPlanCard(chatbotData, 'CHATBOT')}
          </div>
        </div>
      )}

      {/* Usage Meters */}
      {hasAnySubscription && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('billing.usageAndLimits')}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {cmmsData && renderUsageSection(cmmsData, 'CMMS')}
            {chatbotData && renderUsageSection(chatbotData, 'CHATBOT')}
          </div>
        </div>
      )}

      {/* Message Blocks */}
      {chatbotData && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{t('billing.messageBlocks')}</h3>
            <button
              onClick={() => setBlockModalOpen(true)}
              className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              {t('billing.buyMessageBlock')}
            </button>
          </div>

          {chatbotData.activeBlock ? (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-purple-900">{chatbotData.activeBlock.blockName}</span>
                <span className="text-sm text-purple-700">
                  {t('billing.expires', { date: formatDateOnly(chatbotData.activeBlock.expiresAt) })}
                </span>
              </div>
              <div className="w-full bg-purple-200 rounded-full h-2 mb-2">
                <div
                  className="bg-purple-600 h-2 rounded-full"
                  style={{
                    width: `${Math.min(100, (chatbotData.activeBlock.messagesUsed / chatbotData.activeBlock.messagesTotal) * 100)}%`
                  }}
                />
              </div>
              <div className="flex justify-between text-sm text-purple-700">
                <span>{chatbotData.activeBlock.messagesUsed.toLocaleString()} {t('billing.used')}</span>
                <span>{chatbotData.activeBlock.messagesRemaining.toLocaleString()} {t('billing.remaining')}</span>
              </div>
              <div className="mt-3 flex justify-end">
                {chatbotData.activeBlock.cancelled ? (
                  <span className="text-xs text-amber-600 italic">{t('billing.willNotRenew')}</span>
                ) : (
                  <button
                    onClick={() => handleCancelBlock(chatbotData.activeBlock.id)}
                    className="text-xs text-red-500 hover:text-red-700 underline"
                  >
                    {t('billing.cancelAutoRenewal')}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">{t('billing.noActiveBlock')}</p>
          )}
        </div>
      )}

      {/* Buy Block Modal */}
      {blockModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{t('billing.buyMessageBlock')}</h3>
              <button onClick={() => setBlockModalOpen(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            {availableBlocks.length === 0 ? (
              <p className="text-gray-500 text-sm">{t('billing.noBlocksAvailable')}</p>
            ) : (
              <div className="space-y-3">
                {availableBlocks.map(block => (
                  <div key={block.id} className="border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{block.name}</p>
                      <p className="text-sm text-gray-500">{block.messageCount.toLocaleString()} {t('billing.messages')}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">${block.price}</p>
                      <button
                        onClick={() => handleBuyBlock(block.id)}
                        disabled={blockBuying}
                        className="mt-1 px-3 py-1 bg-primary-600 text-white text-sm rounded hover:bg-primary-700 disabled:opacity-50"
                      >
                        {blockBuying ? t('billing.buying') : t('billing.buy')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* CMMS Slot Packs */}
      {cmmsData && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 mt-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('billing.slotPacks')}</h3>

          {[
            { key: 'USER',     labelKey: 'cmms.team_users', current: cmmsData.currentUsers,     limit: cmmsData.includedUsers },
            { key: 'ASSET',    labelKey: 'cmms.assets',     current: cmmsData.currentAssets,    limit: cmmsData.includedAssets },
            { key: 'LOCATION', labelKey: 'cmms.locations',  current: cmmsData.currentLocations, limit: cmmsData.includedLocations },
            { key: 'BRANCH',   labelKey: 'cmms.branches',   current: cmmsData.currentBranches,  limit: cmmsData.includedBranches },
          ].map(({ key, labelKey, current, limit }) => {
            const label = t(labelKey);
            const activeForType = (cmmsData.activeSlots || []).filter(s => s.resourceType === key);
            const totalSlotQty  = activeForType.reduce((sum, s) => sum + s.quantity, 0);
            return (
              <div key={key} className="mb-4 pb-4 border-b border-gray-100 last:border-0 last:mb-0 last:pb-0">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-700">{label}</span>
                  <button
                    onClick={() => openSlotModal(key, label)}
                    className="px-3 py-1 text-sm bg-orange-600 text-white rounded hover:bg-orange-700"
                  >
                    {t('billing.addSlot', { label })}
                  </button>
                </div>
                {activeForType.length > 0 && (
                  <div className="space-y-1 mb-2">
                    {activeForType.map((slot, idx) => (
                      <div key={idx} className="text-sm text-orange-700 bg-orange-50 rounded px-3 py-1 flex justify-between items-center">
                        <span>{t('billing.slotPackQty', { count: slot.quantity, label })} — {slot.packName}</span>
                        <div className="flex items-center gap-2">
                          <span>{t('billing.expires', { date: formatDateOnly(slot.expiresAt) })}</span>
                          {!slot.cancelled ? (
                            <button
                              onClick={() => handleCancelSlot(slot.id)}
                              className="text-xs text-red-500 hover:text-red-700 underline ms-2"
                            >
                              {t('common.cancel')}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-400 italic ms-2">{t('billing.willNotRenewShort')}</span>
                          )}
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-gray-500">
                      {t('billing.slotTotalExtra', { count: totalSlotQty, label: label.toLowerCase() })}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Buy Slot Pack Modal */}
      {slotModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('billing.addSlotPack', { type: slotModal.label })}
              </h3>
              <button onClick={() => setSlotModal({ open: false, resourceType: null })} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            {availableSlotPacks.length === 0 ? (
              <p className="text-gray-500 text-sm">{t('billing.noSlotPacks')}</p>
            ) : (
              <div className="space-y-3">
                {availableSlotPacks.map(pack => (
                  <div key={pack.id} className="border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{pack.name}</p>
                      <p className="text-sm text-gray-500">{t('billing.slotPackQty', { count: pack.quantity, label: slotModal.label })}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">{t('billing.perPeriod', { price: '$' + pack.price })}</p>
                      <button
                        onClick={() => handleBuySlot(pack.id)}
                        disabled={slotBuying}
                        className="mt-1 px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 disabled:opacity-50"
                      >
                        {slotBuying ? t('billing.buying') : t('billing.buy')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Overage Settings */}
      {false && hasAnySubscription && (
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Overage & Additional Usage</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {cmmsData && renderOverageSection(cmmsData, 'CMMS')}
            {chatbotData && renderOverageSection(chatbotData, 'CHATBOT')}
          </div>
        </div>
      )}

      {/* Invoice History */}
      {hasAnySubscription && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">{t('billing.invoiceHistory')}</h2>
          </div>
          <div className="p-6">
            {renderInvoicesTable()}
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (() => {
        const cancelData = cancelServiceType === 'CMMS' ? cmmsData : chatbotData;
        const serviceLabel = cancelServiceType === 'CMMS' ? 'CMMS' : 'AI Chatbot';
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {t('billing.cancelTitle', { service: serviceLabel })}
                    </h2>
                    <p className="text-sm text-gray-500 mt-0.5">{t('billing.cannotUndo')}</p>
                  </div>
                </div>

                {/* Warning */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-5">
                  <p className="text-sm text-red-800 font-medium mb-2">
                    {t('billing.immediatelyLoseAccess')}
                  </p>
                  <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                    {cancelServiceType === 'CMMS' ? (
                      <>
                        <li>{t('billing.cmmsLose1')}</li>
                        <li>{t('billing.cmmsLose2')}</li>
                        <li>{t('billing.cmmsLose3')}</li>
                      </>
                    ) : (
                      <>
                        <li>{t('billing.chatbotLose1')}</li>
                        <li>{t('billing.chatbotLose2')}</li>
                        <li>{t('billing.chatbotLose3')}</li>
                      </>
                    )}
                  </ul>
                </div>

                {/* Plan Info */}
                {cancelData && (
                  <div className="bg-gray-50 rounded-lg p-3 mb-5 text-sm">
                    <div className="flex justify-between text-gray-700">
                      <span className="font-medium">{t('billing.currentPlanLabel')}</span>
                      <span>{cancelData.planName}</span>
                    </div>
                    {cancelData.nextBillingDate && (
                      <div className="flex justify-between text-gray-700 mt-1">
                        <span className="font-medium">{t('billing.paidThrough')}</span>
                        <span>{formatDateOnly(cancelData.nextBillingDate)}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Buttons */}
                <div className="flex flex-col gap-2">
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelLoading}
                    className="w-full py-2.5 px-4 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {cancelLoading ? (
                      <><Loader className="w-4 h-4 animate-spin" /> {t('billing.cancelling')}</>
                    ) : (
                      t('billing.yesCancelSubscription')
                    )}
                  </button>
                  <button
                    onClick={() => setShowCancelModal(false)}
                    disabled={cancelLoading}
                    className="w-full py-2.5 px-4 btn btn-outline font-semibold"
                  >
                    {t('billing.keepSubscription')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Invoice / Receipt Preview Modal */}
      {renderPreviewModal()}

      {/* Upgrade Modal */}
      {renderUpgradeModal()}

      {/* Plan Change Confirm Modal (Paid→Paid) */}
      <PlanChangeConfirmModal
        isOpen={showConfirmModal}
        onClose={() => { setShowConfirmModal(false); setConfirmPreview(null); setChangePlanSubmitting(false); }}
        onConfirm={handleConfirmChangePlan}
        fromPlanName={upgradeServiceType === 'CMMS' ? cmmsData?.planName : chatbotData?.planName}
        fromPlanPrice={upgradeServiceType === 'CMMS' ? Number(cmmsData?.basePrice) : Number(chatbotData?.basePrice)}
        toPlanName={selectedPlan?.planName || selectedPlan?.name}
        toPlanPrice={billingPeriod === 'MONTHLY'
          ? Number(selectedPlan?.monthlyPrice)
          : Number((Number(selectedPlan?.annualPrice) / 12).toFixed(2))}
        billingPeriod={billingPeriod}
        proratedAmount={confirmPreview?.proratedAmount}
        nextBillingDate={confirmPreview?.nextBillingDate}
        paymentMethodBrand={confirmPreview?.paymentMethodBrand}
        paymentMethodLast4={confirmPreview?.paymentMethodLast4}
        isLoading={previewLoading}
        isSubmitting={changePlanSubmitting}
      />
    </div>
  );
};

export default Billing;
